// Operator device lifecycle: give this install a stable identity, report its
// state to operations.operator_devices, and — on Android — silently reconcile
// the running web bundle toward the version an operator has pinned for THIS
// device (per-device targeting; room is not a version input).
//
// Called on app launch and on foreground resume (see main.tsx). The OTA swap is
// applied via Capawesome Live Update, which restarts the webview — so a pending
// update takes effect on the next launch/foreground, never mid-session.

import { databaseService } from './database';
import { getPlatformServices } from './platform';
import { detectPlatform } from '@/utils/platform';
import { compareVersions } from './operatorUpdate';
import type { OperatorPlatform } from '@/types/database';

const DEVICE_ID_KEY = 'operatorDeviceId';

// Guards against launching duplicate OTA downloads when reconcile fires more than
// once before the applying webview restarts (mount + room change + resume).
let otaApplyInFlight = false;

// A stable per-install id. Generated once and persisted via the platform storage
// service (Capacitor Preferences on Android, localStorage on web/electron). This
// is the anchor operators pin a version to.
async function ensureDeviceId(): Promise<string> {
  const storage = getPlatformServices().storage;
  const existing = await storage.get(DEVICE_ID_KEY);
  if (existing) return existing;
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  await storage.set(DEVICE_ID_KEY, id);
  return id;
}

function platformTag(): OperatorPlatform {
  const p = detectPlatform();
  return p === 'electron' ? 'electron' : p === 'capacitor' ? 'android' : 'web';
}

// Version this install is actually running. On Android this is the active Live
// Update bundle id (or the built-in build); elsewhere it's the compiled version.
async function currentVersion(): Promise<string> {
  const update = getPlatformServices().update;
  if (update) {
    try {
      return await update.getVersion();
    } catch {
      // fall through to the compiled version
    }
  }
  return __APP_VERSION__;
}

// This install's identity and the version it is actually running — what the
// Software → Operator devices tab shows so you can tell which row is the device
// in your hands (and, on Android, see an applied OTA take effect).
export async function getThisDevice(): Promise<{ deviceId: string; version: string }> {
  return { deviceId: await ensureDeviceId(), version: await currentVersion() };
}

export interface ReconcileResult {
  deviceId: string;
  currentVersion: string;
  desiredVersion: string | null;
  applying: boolean; // true if an OTA update was kicked off (app will restart)
}

// Register/heartbeat this device and, on Android, apply its pinned version if it
// differs. Returns quietly (never throws) so a backend/storage hiccup can't block
// app startup. `roomId` is the device's currently-selected room — reported as
// metadata only.
export async function reconcileOperatorDevice(roomId?: string | null): Promise<ReconcileResult | null> {
  try {
    const deviceId = await ensureDeviceId();
    const version = await currentVersion();
    const platform = platformTag();

    // Self-report identity + reported state (does not touch operator-set fields).
    await databaseService.reportOperatorDevice({
      deviceId,
      platform,
      currentVersion: version,
      roomId: roomId ?? null,
    });

    const device = await databaseService.getOperatorDevice(deviceId);
    const desired = device?.desired_version ?? null;

    // Only Android does OTA web-bundle reconciliation. Electron self-updates via
    // its installer; web is display-only.
    const canOta = platform === 'android';
    const needsUpdate = !!desired && compareVersions(desired, version) !== 0;

    if (canOta && needsUpdate && desired && !otaApplyInFlight) {
      otaApplyInFlight = true;
      // Lazy import so the plugin is only pulled on the native path.
      const { applyOperatorVersion } = await import('./platform/capacitor');
      // Fire-and-forget: on success the webview restarts on the new bundle.
      applyOperatorVersion(desired).then((r) => {
        if (!r.success) {
          otaApplyInFlight = false; // allow a retry on the next trigger
          console.warn(`[operator-update] OTA to ${desired} failed: ${r.error}`);
        }
      });
      return { deviceId, currentVersion: version, desiredVersion: desired, applying: true };
    }

    return { deviceId, currentVersion: version, desiredVersion: desired, applying: false };
  } catch (err) {
    console.warn('[operator-update] reconcile skipped:', err);
    return null;
  }
}
